"use client"

import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, CircularProgress, OutlinedInput, Typography } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import crypto from 'crypto';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const decryptKey = (key, secret) => {
	const [ivHex, encryptedHex] = key.split(':');
	const iv = Buffer.from(ivHex, 'hex');
	const validSecret = crypto.createHash('sha256').update(secret).digest();
	const decipher = crypto.createDecipheriv('aes-256-cbc', validSecret, iv);
	let decrypted = decipher.update(encryptedHex, 'hex', 'utf8');
	decrypted += decipher.final('utf8');
	return decrypted;
};

const decryptPDF = (encryptedPdf, key, ivHex) => {
	const validKey = crypto.createHash('sha256').update(key).digest();
	const iv = Buffer.from(ivHex, 'hex');
	const decipher = crypto.createDecipheriv('aes-256-cbc', validKey, iv);
	let decrypted = decipher.update(encryptedPdf, 'binary', 'binary');
	decrypted += decipher.final('binary');
	return Buffer.from(decrypted, 'binary');
};

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Reader = ({ koleksi, accessToken }) => {
	const [pdfData, setPdfData] = useState<Buffer | null>(null);
	const [loading, setLoading] = useState(true);
	const [numPages, setNumPages] = useState(null);
	const [pageNumber, setPageNumber] = useState(1);
	const [scale, setScale] = useState(1.0);

	useEffect(() => {
		const fetchAndDecryptPDF = async () => {
			try {
				setLoading(true);
				const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/v2/koleksi/${koleksi.id}/read`, {
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				});

				if (!response.ok) {
					throw new Error('Failed to fetch PDF');
				}

				const randomKey = response.headers.get('X-SBO-Random-Key');
				const ivHex = response.headers.get('X-SBO-IV');

				let decryptedKey = randomKey;
				decryptedKey = decryptKey(decryptedKey, process.env.NEXT_PUBLIC_KEY_2);
				decryptedKey = decryptKey(decryptedKey, process.env.NEXT_PUBLIC_KEY_1);

				const encryptedPdf = await response.arrayBuffer();
				const decryptedPdf = decryptPDF(Buffer.from(encryptedPdf), decryptedKey, ivHex);

				setPdfData(decryptedPdf);
			} catch (error) {
				console.error('Error fetching or decrypting PDF:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchAndDecryptPDF();
	}, [koleksi.id]);

	const memoizedPdfData = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);

	function onDocumentLoadSuccess({ numPages }) {
		setNumPages(numPages);
		setPageNumber(1);
	}

	function changePage(offset) {
		setPageNumber(prevPageNumber => prevPageNumber + offset);
	}

	function previousPage() {
		changePage(-1);
	}

	function nextPage() {
		changePage(1);
	}

	return (
		<Box sx={{
			display: 'flex',
			flex: 1,
			flexDirection: 'column',
		}}>
			{loading ? (
				<Box sx={{
					display: 'flex',
					flex: 1,
					alignItems: 'center',
					justifyContent: 'center',
				}}>
					<CircularProgress />
				</Box>
			) : (
				<>
					<Box sx={{
						position: 'sticky',
						top: 0,
						backgroundColor: 'white',
						zIndex: 1000,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: 1,
						gap: 1,
					}}>
						<Button type="button" disabled={pageNumber <= 1} onClick={previousPage} variant='contained' size='small'>
							Previous
						</Button>
						<Box sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
						}}>
							<OutlinedInput
								value={pageNumber}
								onChange={(e) => setPageNumber(Number(e.target.value))}
								type="number"
								inputProps={{ min: 1, max: numPages || 1 }}
								size="small"
							/>
							<Typography>
								of {numPages || '--'}
							</Typography>
						</Box>
						<Button type="button" disabled={numPages !== null && pageNumber >= numPages} onClick={nextPage} variant='contained' size='small'>
							Next
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setScale(prevScale => prevScale + 0.1)}
						>
							Scale +
						</Button>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setScale(prevScale => Math.max(0.1, prevScale - 0.1))}
						>
							Scale -
						</Button>
					</Box>
					<Box sx={{
						display: 'flex',
						flex: 1,
						alignItems: 'center',
						justifyContent: 'center',
						m: 2
					}}>
						{memoizedPdfData && (
							<Document file={memoizedPdfData} onLoadSuccess={onDocumentLoadSuccess} scale={scale}>
								<Page pageNumber={pageNumber} />
							</Document>
						)}
					</Box>
				</>
			)}
			<style>
				{`
					.react-pdf__Page__canvas {
						box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
					}
				`}
			</style>
		</Box>
	);
};

export default Reader;